import { IndicatorIcon } from '@vizit/icons';
import * as React from 'react';
import {
  CHART_COLOR_AMBER,
  CHART_COLOR_BG_TRANSPARENT,
  CHART_COLOR_GRAY,
  CHART_COLOR_GREEN,
  CHART_COLOR_INDICATOR,
} from './colors';
import type { BarMetric } from './types';

interface BarChartProps {
  metric: BarMetric;
  /**
   * Width of the chart
   * @default 300
   */
  width?: number;
  /**
   * Height of the chart
   * @default 60
   */
  height?: number;
}

const PAD_L = 4;
const PAD_R = 4;
const PAD_T = 6;
const PAD_B = 6;

type Region = {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  isOptimal: boolean;
};

/**
 * Renders a bar chart with connected step-area regions
 * Optimal band rendered as tallest plateau
 * Only the region containing the current value is colored (green = optimal, amber = off-target)
 */
const BarChart = React.forwardRef<SVGSVGElement, BarChartProps>((props, forwardedRef) => {
  const { metric, width = 300, height = 60 } = props;
  const { range, optimalBand, currentValue } = metric;

  const clampedValue = Math.max(range.min, Math.min(range.max, currentValue));

  const chartW = width - PAD_L - PAD_R;
  const chartH = height - PAD_T - PAD_B;
  const baseY = PAD_T + chartH;
  const rangeSpan = range.max - range.min;

  const bandMin = Math.min(...optimalBand);
  const bandMax = Math.max(...optimalBand);

  const toX = (val: number) => PAD_L + ((val - range.min) / rangeSpan) * chartW;

  const optimalTop = baseY - chartH * 0.7;
  const offTargetTop = baseY - chartH * 0.4;

  const makeRegion = (dataMin: number, dataMax: number, isOptimal: boolean): Region => {
    const x1 = toX(dataMin);
    const x2 = toX(dataMax);
    if (isOptimal) {
      return { x1, x2, y1: optimalTop, y2: optimalTop, isOptimal: true };
    }
    return {
      x1,
      x2,
      y1: dataMin === range.min ? offTargetTop * 1.15 : offTargetTop,
      y2: dataMax === range.max ? offTargetTop * 1.15 : offTargetTop,
      isOptimal: false,
    };
  };

  const tolerance = rangeSpan * 0.05;
  const atLeft = Math.abs(bandMin - range.min) < tolerance;
  const atRight = Math.abs(bandMax - range.max) < tolerance;

  const regions: Region[] = [];
  if (atLeft && atRight) {
    regions.push(makeRegion(range.min, range.max, true));
  } else if (atLeft) {
    regions.push(makeRegion(range.min, bandMax, true));
    regions.push(makeRegion(bandMax, range.max, false));
  } else if (atRight) {
    regions.push(makeRegion(range.min, bandMin, false));
    regions.push(makeRegion(bandMin, range.max, true));
  } else {
    regions.push(makeRegion(range.min, bandMin, false));
    regions.push(makeRegion(bandMin, bandMax, true));
    regions.push(makeRegion(bandMax, range.max, false));
  }

  const isInteger = optimalBand.every((v) => Number.isInteger(v));
  const isInOptimal = isInteger
    ? optimalBand.includes(Math.round(currentValue))
    : currentValue >= bandMin && currentValue <= bandMax;

  // Special case: if optimal band extends to range.max and current value exceeds it,
  // treat as optimal since we can't know if higher is better without more reference data
  const treatAsOptimal = isInOptimal || (atRight && currentValue > range.max);

  const activeIdx = treatAsOptimal
    ? regions.findIndex((r) => r.isOptimal)
    : currentValue < bandMin
      ? 0
      : regions.length - 1;

  // Indicator: use the active region for correct Y positioning
  // Use clamped value for positioning so indicator always appears on chart
  const indicatorX = toX(clampedValue);
  const indicatorRegion = regions[activeIdx];

  let indicatorY: number;
  if (indicatorRegion) {
    const t =
      indicatorRegion.x2 !== indicatorRegion.x1
        ? (indicatorX - indicatorRegion.x1) / (indicatorRegion.x2 - indicatorRegion.x1)
        : 0;
    indicatorY = indicatorRegion.y1 + t * (indicatorRegion.y2 - indicatorRegion.y1);
  } else {
    indicatorY = offTargetTop;
  }
  indicatorY -= 2;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      ref={forwardedRef}
      className="w-full h-auto"
      aria-label={`Bar chart showing current value ${currentValue}`}
    >
      <rect width={width} height={height} rx="6" fill={CHART_COLOR_BG_TRANSPARENT} />
      {regions.map((r, i) => {
        const fill = i === activeIdx ? (treatAsOptimal ? CHART_COLOR_GREEN : CHART_COLOR_AMBER) : CHART_COLOR_GRAY;

        if (r.isOptimal) {
          return (
            <rect key={`${r.x1}-${r.x2}`} x={r.x1} y={r.y1} width={r.x2 - r.x1} height={baseY - r.y1} fill={fill} />
          );
        }

        const points = `${r.x1},${r.y1} ${r.x2},${r.y2} ${r.x2},${baseY} ${r.x1},${baseY}`;
        return <polygon key={`${r.x1}-${r.x2}`} points={points} fill={fill} />;
      })}

      <g transform={`translate(${indicatorX - 2}, ${indicatorY - 10})`}>
        <IndicatorIcon size={10} color={CHART_COLOR_INDICATOR} />
      </g>
    </svg>
  );
});

export type { BarChartProps };
export { BarChart };
